export const useUi = () => {
  /**
   * 자릿수에 맞는 반올림
   * @param unit 반올림할 숫자
   * @param digit 자릿수
   * @returns
   */
  function roundUnitCount(unit: number, digit: number) {
    const result = Math.round(unit * digit) / digit
    return result
  }

  /**
   * 자릿수에 맞는 랜덤 숫자 생성
   * @param digit 자릿수
   * @returns
   */
  function randomNumber(digit: number): string {
    let value = ''
    for (let i = 0; i < digit; i++) {
      value += String(Math.floor(Math.random() * 10))
    }
    return value
  }

  /**
   * 3단위 콤마
   * @example 1000000 -> 1,000,000
   * @param val 숫자
   * @returns
   */
  function comma(val: number | string) {
    return String(val).replace(/(\d)(?=(?:\d{3})+(?!\d))/g, '$1,')
  }

  function commaWon(val: number | string) {
    return val ? String(val).replace(/(\d)(?=(?:\d{3})+(?!\d))/g, '$1,') + '원' : ' 0 원'
  }

  /**
   * 3단위 콤마 제거
   * @param val 숫자
   * @returns
   */
  function uncomma(val: string | number) {
    return String(val).replace(/[^\d]+/g, '')
  }

  /**
   * 제공되는 단위로 올림, 반올림, 내림
   * @example digitsRoundUp(1234, 'round', 100) -> 1200
   * @example digitsRoundUp(1234, 'ceil', 100) -> 1300
   * @example digitsRoundUp(1234, 'floor', 100) -> 1200
   * @param value 값
   * @param method 'round' | 'ceil' | 'floor'
   */
  function digitsRoundUp(value: number, method: 'round' | 'ceil' | 'floor', digits: number) {
    let result = 0
    switch (method) {
      case 'round':
        result = Math.round(value / digits) * digits
        break
      case 'ceil':
        result = Math.ceil(value / digits) * digits
        break
      case 'floor':
        result = Math.floor(value / digits) * digits
        break
    }
    return result
  }

  /**
   * 바이트로 제공되는 용량을 GB로 변환
   * @example convertQuota(1000) -> 1GB
   * @example convertQuota(500) -> 0.5GB
   * @param quota 바이트 단위 용트
   * @returns gb 단위 용량
   */
  function convertQuota(quota: number): string {
    if (!quota) {
      return '?GB'
    }
    const gb = Math.floor(quota / 1000)
    const mb = Math.floor((quota % 1000) / 100)
    if (gb === 0) {
      return `${mb * 100}MB`
    } else {
      return `${gb}${mb ? `.${mb}` : ''}GB`
    }
  }

  const checkEmail = (val: string) => {
    const regEmail = /^([0-9a-zA-Z_.-]+)@([0-9a-zA-Z_-]+)(\.[0-9a-zA-Z_-]+){1,2}$/
    if (!regEmail.test(val)) {
      return false
    } else {
      return true
    }
  }

  /**
   * 공백없이 숫자, 영문자만 허용
   */
  const checkString = (val: string) => {
    const inputRegex = /^[0-9a-zA-Z]+$/
    return inputRegex.test(val)
  }

  function removeHyphen(val: string) {
    return val.replace(/-/g, '')
  }

  /**
   * 010으로 시작하는 11자리 숫자만 허용
   */
  function checkPhoneNumber(val: string): boolean {
    if (!val) {
      return false
    }
    return val.startsWith('010') && val.length === 11 && /^[0-9]+$/.test(val)
  }

  function formatHyphen(val: string) {
    return val.replace(/[^0-9]/g, '').replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
  }

  /**
   * 단위 변환(데이터 사용량)
   * @param dataInMb MB 단위 데이터
   * 1024MB 이상 GB단위로 변환(소수점 1자리까지 표기, 소수점이 0일경우 표기하지 않음)
   * 1024MB 미만은 MB로 표기
   */
  function formatGB(dataInMb: number | string) {
    const sizeInMb = Number(dataInMb)
    const gb = 1024
    if (sizeInMb >= gb) {
      const formattedSize = sizeInMb / gb
      return `${formattedSize % 1 === 0 ? formattedSize.toFixed(0) : formattedSize.toFixed(1)}GB`
    } else {
      return `${Math.floor(sizeInMb)}MB`
    }
  }

  /**
   * 날짜 포맷 변환
   * @param date ISOString
   */
  function textDateFormat(date: string, spaceText: boolean, shortText: boolean) {
    const clone = date.split('T')[0].split('-')
    let res = ''
    if (spaceText) {
      res = String(clone[0]).concat('년 ', clone[1], '월 ', clone[2], '일')
      return res
    } else if (shortText) {
      res = String(clone[0]).slice(2).concat('년', clone[1], '월', clone[2], '일')
      return res
    } else {
      res = String(clone[0]).concat('년', clone[1], '월', clone[2], '일')
      return res
    }
  }

  /**
   * base64 인코딩
   */
  function encodeBase64(value: string) {
    if (process.client) {
      return window.btoa(unescape(encodeURIComponent(value)))
    } else {
      return Buffer.from(value, 'ascii').toString('base64')
    }
  }

  /**
   * base64 디코딩
   */
  function decodeBase64(value: string) {
    if (process.client) {
      return decodeURIComponent(escape(window.atob(value)))
    } else {
      return Buffer.from(value, 'base64').toString('ascii')
    }
  }

  /**
   * 입력된 날짜에 월, 일을 더한 날짜를 반환
   * @param date 기준 날짜
   * @param param 하이픈 포함 여부
   * @param afterMonth 더할 월
   * @param afterDay 더할 일
   */
  function dateFormat(date: Date, param: boolean, afterMonth: number, afterDay: number) {
    let setDate = date
    if (afterMonth) {
      setDate = new Date(date.setMonth(date.getMonth() + afterMonth))
    }
    if (afterDay) {
      setDate = new Date(date.setDate(date.getDate() + afterDay))
    }
    if (!param) {
      return getHyphenDate(setDate)
    }
    return getHyphenDate(setDate).replaceAll('-', '')
  }

  /**
   * 두 날짜 사이의 일수 계산
   */
  function countDate(dDay: Date | string, compareDate?: Date | string) {
    const compare = compareDate ? new Date(compareDate) : new Date()
    const gap = new Date(dDay).getTime() - compare.getTime()
    return Math.ceil(gap / (1000 * 60 * 60 * 24))
  }

  /**
   * 날짜 비교
   * @param targetDateTime ISOString
   * @param compareDateTime ISOString
   * @returns
   */
  function checkCompareDate(targetDateTime: string, compareDateTime: string) {
    return targetDateTime <= compareDateTime
  }

  /**
   * 남은 시간 계산
   * @param date 2021-01-01T00:00:00
   * @returns
   */
  function countDownTotalTime(date: string) {
    const countDownDate = new Date(date).getTime()
    const now = new Date().getTime()
    return countDownDate - now
  }

  /**
   * 남은 시간 계산
   * @param milliSec 밀리초
   * @returns
   */
  function countDown(milliSec: number) {
    if (!milliSec) {
      return
    }
    const days = String(Math.floor(milliSec / (86400 * 1000)))
    const hours = String(Math.floor((milliSec % (86400 * 1000)) / (3600 * 1000)))
    const minutes = String(Math.floor((milliSec % (3600 * 1000)) / (60 * 1000)))
    const seconds = String(Math.floor((milliSec % (60 * 1000)) / 1000))
    return String(hours).concat('시간 ', minutes, '분 ', seconds, '초 (', days, ' 일)')
  }

  /**
   * 날짜(ISOString)를 .으로 구분하여 반환
   * @param val 2021-01-01T00:00:00
   * @returns 2021.01.01
   */
  function dotDateFormat(val: string) {
    const clone = val.split('T')[0].split('-')
    return clone[0].concat('.', clone[1], '.', clone[2])
  }

  /**
   * 날짜/시간 문자열을 .으로 구분하여 반환
   * @param val 2021-01-01T00:00:00, | 2021-01-01 00:00:00
   * @returns 2021.01.01 00:00
   */
  function dotDateTimeFormat(val: string) {
    const string = val.replace('T', ' ')
    const splitDateTime = string.split(' ')
    const splitDate = splitDateTime[0].split('-')
    const splitTime = splitDateTime[1].split(':')
    return `${splitDate[0]}.${splitDate[1]}.${splitDate[2]} ${splitTime[0]}:${splitTime[1]}`
  }

  /**
   * 날짜를 -로 구분하여 반환
   * @example 2021-01-01
   */
  function getHyphenDate(date: Date) {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  /**
   * 쿠폰날짜를 반환
   * @example 21.01.01
   */
  function getCouponDate(val: string) {
    const clone = val.split('T')[0].split('-')
    return clone[0].slice(2, 4).concat('.', clone[1], '.', clone[2])
  }
  /**
   * 숫자를 시간 표시로 반환
   * @param val '14'
   * @returns  '14:00'
   */

  function getTimeByNumber(time: string | number) {
    return `${time}:00`
  }

  // 2024-03-21T23:20:00.000Z 형식을 2024.03.21 오후 11:20 형식으로 변환
  function translateDateTime(date: string) {
    const newDate = new Date(date)
    const year = newDate.getFullYear()
    const month = ('0' + (newDate.getMonth() + 1)).slice(-2)
    const day = ('0' + newDate.getDate()).slice(-2)
    const hour = ('0' + newDate.getHours()).slice(-2)
    const minute = ('0' + newDate.getMinutes()).slice(-2)
    const ampm = newDate.getHours() >= 12 ? '오후' : '오전'
    return `${year}.${month}.${day} ${ampm} ${hour}:${minute}`
  }

  return {
    roundUnitCount,
    randomNumber,
    comma,
    uncomma,
    commaWon,
    digitsRoundUp,
    convertQuota,
    checkEmail,
    checkPhoneNumber,
    textDateFormat,
    dateFormat,
    countDate,
    checkCompareDate,
    countDownTotalTime,
    countDown,
    dotDateFormat,
    dotDateTimeFormat,
    encodeBase64,
    decodeBase64,
    getHyphenDate,
    getCouponDate,
    getTimeByNumber,
    checkString,
    translateDateTime,
    formatGB,
    formatHyphen,
    removeHyphen
  }
}
